// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import type { InvoiceLifecycleRow as ApiInvoiceLifecycleRow } from '@/billing/api/get-invoice-lifecycle-listing';
import type { CurrencyInvoiceLifecycleSummary } from '@/billing/api/get-invoice-lifecycle-summary';
import type { SegmentKey } from '@/billing/lifecycle/segments';
import type { IsoCurrencyCode } from '@/currency/constants/iso';
import { Decimal } from 'decimal.js';
import type { DateTime } from 'luxon';

// Per-segment carrying cost breakdown for a single invoice row. A field
// is null when the segment doesn't apply; "0" means the segment applies
// but its compound interest factor collapses to zero (e.g. a same-day
// stage transition). Negative values are carrying gains — the compound
// formula `amount * (annualFactor^(days / 365) - 1)` preserves the sign
// of both the amount and the day delta. Date lag is absent because it
// has no carrying-cost contribution (label gap, not a waiting interval).
export interface LifecycleCarryingCostBreakdown {
  chargeLag: Decimal | null;
  invoiceReview: Decimal | null;
  deliveryLag: Decimal | null;
  actualTerms: Decimal | null;
  delinquency: Decimal | null;
}

// Numeric view of a single billing-lifecycle detail row. All numeric
// fields are `number | null` (null = not-yet-populated for this invoice).
// Anchor dates are parsed at the API boundary and passed through here.
export interface LifecycleRow {
  invoiceKey: string;
  invoiceNumber: string;
  customer: string | null;
  project: string | null;
  amount: Decimal;
  currency: IsoCurrencyCode;
  chargeLag: number | null;
  invoiceReview: number | null;
  deliveryLag: number | null;
  dateLag: number | null;
  actualTerms: number | null;
  delinquency: number | null;
  totalCycle: number | null;
  carryingCost: Decimal | null;
  carryingCostBreakdown: LifecycleCarryingCostBreakdown;
  billingEligibleDate: DateTime | null;
  draftedAt: DateTime | null;
  openedAt: DateTime | null;
  firstSentAt: DateTime | null;
  invoiceDate: DateTime | null;
  dueDate: DateTime | null;
  paidDate: DateTime | null;
}

// Aggregate summary for a single segment — the numbers that power the
// summary cards and the aggregate Gantt. `avgDays` is null when no
// invoice in the window contributed an observation for the segment;
// the carrying-cost fields are null in the same scenario (there's no
// input to compound), so the summary card renders an em-dash instead
// of a fabricated zero.
export interface SegmentSummary {
  key: SegmentKey;
  avgDays: number | null;
  invoiceCount: number;
  totalCarryingCost: Decimal | null;
  annualizedCarryingCost: Decimal | null;
}

// The seven summary keys that produce rendered cards / Gantt bars.
// Total cycle is the headline aggregate; the six segment keys render
// both as cards (grouped by ADS / ADC cluster) and as rows on the
// aggregate Gantt.
type SummarySegmentKey = Exclude<
  keyof CurrencyInvoiceLifecycleSummary,
  'p90CycleDays' | 'isoCode'
>;

export const SUMMARY_SEGMENT_KEYS: SummarySegmentKey[] = [
  'chargeLag',
  'invoiceReview',
  'deliveryLag',
  'dateLag',
  'actualTerms',
  'delinquency',
  'totalCycle',
];

// Maps an API row into the shape the rendering code expects. Null day
// values pass through untouched so downstream UI can render an em-dash.
// Anchor dates pass through as DateTime values from the API schema.
export function apiRowToLifecycleRow(
  row: ApiInvoiceLifecycleRow
): LifecycleRow {
  return {
    invoiceKey: row.invoiceKey,
    invoiceNumber: row.invoiceNumber,
    customer: row.customerName,
    project: row.projectName,
    amount: row.amount.amount,
    currency: row.currency,
    chargeLag: row.chargeLag,
    invoiceReview: row.invoiceReview,
    deliveryLag: row.deliveryLag,
    dateLag: row.dateLag,
    actualTerms: row.actualTerms,
    delinquency: row.delinquency,
    totalCycle: row.totalCycle,
    carryingCost: row.carryingCost,
    carryingCostBreakdown: row.carryingCosts,
    billingEligibleDate: row.billingEligibleDate,
    draftedAt: row.draftedAt,
    openedAt: row.openedAt,
    firstSentAt: row.firstSentAt,
    invoiceDate: row.invoiceDate,
    dueDate: row.dueDate,
    paidDate: row.paidDate,
  };
}

// Flattens one currency bucket's segment-keyed summary into the ordered
// array the summary cards and aggregate Gantt consume.
export function apiSummaryToSegmentSummaries(
  summary: CurrencyInvoiceLifecycleSummary
): SegmentSummary[] {
  return SUMMARY_SEGMENT_KEYS.map((key) => {
    const api = summary[key];
    return {
      key,
      avgDays: api.avgDays,
      invoiceCount: api.invoiceCount,
      totalCarryingCost: api.totalCarryingCost,
      annualizedCarryingCost: api.annualizedCarryingCost,
    };
  });
}
